<script lang="ts">
  import Loader from '$shared/atoms/Loader/Loader.svelte';
  import { sentry } from '$shared/helpers/logger/sentry';
  import { ComponentException } from '$shared/exceptions/ComponentExpection';
  import SimpleSensitiveText from '$shared/atoms/SimpleSensitiveText/SimpleSensitiveText.svelte';
  import type { SensitiveString } from '$provider/models/SensitiveString';
  import { onMount } from 'svelte';

  interface Props {
    sensitiveData: SensitiveString;
    hasObfuscatedLean?: boolean;
    showIcon?: boolean;
    noPermission?: boolean;
  }

  let {
    sensitiveData,
    hasObfuscatedLean = false,
    showIcon = true,
    noPermission = false
  }: Props = $props();

  const notObfuscatedCount = 4;

  let active = $state(false);
  let loader = $state(false);
  let error = $state(null);

  let isUnmasked = $derived(!!sensitiveData?.isUnmasked);

  $effect(() => {
    !isUnmasked && (active = false);
  });

  onMount(() => {
    if (noPermission) return;

    if (hasObfuscatedLean && sensitiveData?.value) {
      (async () => {
        await unmaskSensitveString();
      })();
    }
  });

  const onClickAction = async (): Promise<void> => {
    error = null;
    active = !active;
    await unmaskSensitveString();
  };

  const unmaskSensitveString = async () => {
    try {
      if (!isUnmasked) {
        loader = true;
        await sensitiveData?.unmaskAsync();
        isUnmasked = true;
        loader = false;
      }
    } catch (err: unknown) {
      error = 'Failed!';
      const exception = ComponentException.from(
        'SensitiveText',
        'unmaskSensitveString',
        {
          sensitiveData,
          hasObfuscatedLean,
          showIcon
        },
        err,
        {
          component: 'SensitiveText',
          value: sensitiveData?.value
        }
      );
      sentry.log(exception);
    } finally {
      loader = false;
    }
  };

  const getObfuscatedString = (value: string): string => {
    const regex = new RegExp(`(\\w)(?=(\\w| ){${notObfuscatedCount}})`, 'g');
    // Replace '-' special character to empty space.
    const label = value.replace(/-/g, ' ');
    // Replace char to mid dot except last 4 character.
    return label.replace(regex, '•');
  };
</script>

<div data-cy="sensitive-content" class="comp-sensitive-text">
  {#if active && loader}
    <div data-cy="sensitive-loader" class="loader"><Loader /></div>
  {:else if error}
    <div class="error">{error}</div>
  {:else if !active && hasObfuscatedLean && sensitiveData?.isUnmasked}
    <div data-cy="sensitive-value" class="value">
      {getObfuscatedString(sensitiveData?.value)}
    </div>
  {:else}
    <SimpleSensitiveText
      {active}
      showIcon={!noPermission && showIcon}
      value={sensitiveData?.value}
      onclick={onClickAction}
    />
  {/if}
</div>

<style>
  .comp-sensitive-text {
    display: flex;
    align-items: center;
    height: 20px;
  }
  .comp-sensitive-text .value {
    white-space: nowrap;
  }

  .comp-sensitive-text .loader {
    width: 50px;
  }
  .error {
    color: var(--red);
  }
</style>
